import {
  BiPlan,
  PlanTypeClick,
  BiStepNames,
  PaymentCycle_BIMap,
} from 'services/BiLogger/BiLoggerTypes';
import {
  ProductFamily,
  Experiments,
  CurrentPlanInOfferingMode,
  PaymentCycle,
  PlanType,
  PurchaseMode,
  Vertical,
  RealEstateId,
  SEPA_ENABLED_COUNTRIES,
  Country,
  Language,
  ProductChangeType,
} from '../enums';
import { IStorage } from 'services/Storage/Storage';
import { Utils } from 'services/utils';
import {
  plansPageTabs,
  taxIncludedCountries,
  WEBSITE_VERTICALS,
} from '../constants';
import {
  ActionTypeReporter,
  AdvancedPlansProductData,
  IAppContext,
  ProductData,
  ChangePlanProductProperties,
  BaseProduct,
  ParsedAppIntentElements,
  VoucherSettings,
} from '../types';
import { action, computed, observable } from 'mobx';
import { SentryLogger } from '../services/SentryLogger';
import { ExperimentsProviderOptions } from '@wix/wix-experiments-react';
import type { Cycle } from '@wix/ambassador-premium-store/types';
import { AdvancedPlansProductModel } from 'models/AdvancedPlansProductModel';
import { throttle } from 'utils';
import { VouchersType } from '@wix/ambassador-premium-vouchers/types';
import { PaymentStatus } from '@wix/ambassador-premium-data-view-retriever-server/types';

export const APP_STEPS = [
  'app.steps.step1',
  'app.steps.step2',
  'app.steps.step3',
];

export const ADVANCED_PLANS_APP_STEPS = [
  'Advanced.Plans_Steps_Step1',
  'Advanced.Plans_Steps_Step2',
  'Advanced.Plans_Steps_Step3',
];

export enum STEP_ACTIONS {
  INCREMENT = 'increment',
  DECREMENT = 'decrement',
  SPECIFIC = 'specific',
}

export class AppStore {
  @observable currentStep = 0;
  @observable previousStep = 0;
  @observable currentProduct: ProductData | AdvancedPlansProductData;
  @observable scrollToTopRef;
  @observable changePlanModalOpened = false;
  @observable planType = PlanType.Website;
  @observable currentCycle = PaymentCycle.YEARLY;
  @observable alternativeCycle: Cycle | undefined = undefined;
  @observable isNoWebsiteModalOpened = false;
  @observable isWizardHidden = false;
  baseUrl = '/';
  isEditorX: boolean = false;

  couponCode?: string;

  experiments: ExperimentsProviderOptions['experiments'];
  totalSteps = 3;
  storage: IStorage;

  constructor(
    public appContext: IAppContext,
    private readonly changeStepThrottle = 700,
  ) {
    this.experiments = appContext.experiments;
    this.storage = appContext.storage;
    this.isEditorX = this.appContext.isEditorXOffering;
    this.planType = this.getLandingTab();

    if (!appContext.debug) {
      window.addEventListener('beforeunload', this.handlePageUnload);
    }
    this.currentProduct = this.appContext.advancedPlansProducts[0];
  }

  private readonly handlePageUnload = (e: Event) => {
    const confirmationMessage = '';
    e.preventDefault();
    e.returnValue = true; // Gecko, Trident, Chrome 34+
    return confirmationMessage; // Gecko, WebKit, Chrome <34
  };

  removeUnloadListener = () => {
    window.removeEventListener('beforeunload', this.handlePageUnload);
  };

  @action.bound
  private incrementStep() {
    return this.currentStep++;
  }

  @action.bound
  private decrementStep() {
    return this.currentStep--;
  }

  @action.bound
  setScrollToTopRef(ref: any) {
    this.scrollToTopRef = ref;
  }

  @action.bound
  setStep(step: number) {
    this.currentStep = step;
  }

  @action.bound
  setPlanType(planType: PlanType) {
    this.planType = planType;
  }

  @action.bound
  openChangePlanModal() {
    this.changePlanModalOpened = true;
  }

  @action.bound
  closeChangePlanModal() {
    this.changePlanModalOpened = false;
  }

  @action.bound
  activateDirectChecktOut() {
    this.hideStepWizard();
    this.setStep(2);
  }

  @action.bound
  hideStepWizard() {
    this.isWizardHidden = true;
  }

  private notifyBIOnCycleChange() {
    this.appContext.biLogger &&
      this.appContext.biLogger.logSelectCycleBiEvent({
        cycleId: this.currentCycle,
        productId: this.currentProduct.id,
        productGuid: this.currentProduct.guid,
        cycle: PaymentCycle_BIMap[this.currentCycle],
      });
  }

  @action
  handleOnBackButtonClick = () =>
    this.changeStep(STEP_ACTIONS.DECREMENT, (from, to) => {
      this.appContext.biLogger.logStepBackBiEvent({
        fromStep: `change_${to}_from_${from}_back`,
        productId: this.currentProduct.id,
        productGuid: this.currentProduct.guid,

        cycle: PaymentCycle_BIMap[this.currentCycle],
      });
    });

  @action
  selectTab = (tab: PlanType, clickType: PlanTypeClick = PlanTypeClick.Top) => {
    this.setPlanType(tab);

    if (this.isInMobileNewExperience) {
      this.setCurrentProduct(this.defaultProductSelection);
    }

    const pricing = this.productsPrices;
    this.appContext.biLogger.logChangeTabBiEvent({
      clickType,
      ppType: tab,
      pricing,
      currency: this.appContext.currencyCode,
    });
  };

  @action
  jumpToTab = (goToPlan: PlanType) => {
    this.selectTab(plansPageTabs[plansPageTabs.indexOf(goToPlan)]);
  };

  @action.bound
  setCurrentCycle({
    cycle,
    sendBiEvent = true,
  }: {
    cycle: PaymentCycle;
    sendBiEvent?: boolean;
  }) {
    this.currentCycle = cycle;
    sendBiEvent && this.notifyBIOnCycleChange();
  }

  @action.bound
  setAlternativeCycle(alternativeCycle?: Cycle) {
    this.alternativeCycle = alternativeCycle;
  }

  @action.bound
  setCouponCode(couponCode: string) {
    this.couponCode = couponCode;
  }

  @action
  setNoWebsiteModal = (val: boolean) => {
    this.isNoWebsiteModalOpened = val;
  };

  scrollToTop = () => {
    this.scrollToTopRef &&
      this.scrollToTopRef.scrollIntoView &&
      this.scrollToTopRef.scrollIntoView({ behavior: 'smooth' });
  };

  @action.bound
  setCurrentProduct(product: ProductData | AdvancedPlansProductData) {
    this.currentProduct = product;
    const enabledCycles = product.cycles
      .filter((cycle) => !cycle.isCycleDisabledInProduct)
      .map((cycle) => cycle.cycleId);
    if (enabledCycles.includes(PaymentCycle.YEARLY)) {
      this.currentCycle = PaymentCycle.YEARLY;
    } else {
      this.currentCycle = enabledCycles[enabledCycles.length - 1];
    }
  }

  @computed
  get isBusinessVertical() {
    return (
      WEBSITE_VERTICALS.indexOf(Vertical[this.appContext.verticalName]) === -1
    );
  }

  @computed
  get isBusinessTab() {
    const {
      requestParamsMap,
      currentPlanDetails,
      advancedPlansProducts,
    } = this.appContext;

    if (
      this.currentPlanInOfferingMode ===
      CurrentPlanInOfferingMode.ProductFullMatch
    ) {
      return currentPlanDetails!.type === PlanType.Business;
    }

    const isChangePlanBusiness =
      this.isChangePlan && currentPlanDetails!.type === PlanType.Business;

    const _products: BaseProduct[] = advancedPlansProducts;

    return (
      isChangePlanBusiness ||
      (!this.isUseDealerInegrationIntentElementsEnabled &&
        this.isBusinessVertical) ||
      requestParamsMap.businessTab === 'true' ||
      _products.filter(
        (product) =>
          product.type === PlanType.Website &&
          product.isEnabledForSecondUpgrade,
      ).length === 0
    );
  }

  @computed
  get productsPrices() {
    const { advancedPlansProducts } = this.appContext;
    const prices = advancedPlansProducts
      .filter((product) => product.type === this.planType)
      .map((product) => {
        const yearlyCycle = product.cycles.find(
          (cycle) => cycle.cycleId === PaymentCycle.YEARLY,
        );
        const monthlyActualPrice = yearlyCycle?.monthlyActualPrice ?? NaN;
        const monthlyFullPrice = yearlyCycle?.monthlyFullPrice ?? NaN;
        const biData: BiPlan = {
          price: monthlyActualPrice,
          subtitle: `premium_plan.${product.productCode}.description`,
          title: product.productName,
          id: product.id,
          discount: monthlyFullPrice - monthlyActualPrice,
        };
        return biData;
      });
    prices.sort((prod1, prod2) => prod2.price - prod1.price);
    return prices;
  }

  @computed
  get isInSale(): boolean {
    return this.isInSeasonalSale || this.isInPersonalSale;
  }

  @computed
  get isInPersonalSale(): boolean {
    return [
      PurchaseMode.PERSONAL_SALE_AGE_1,
      PurchaseMode.PERSONAL_SALE_AGE_2,
      PurchaseMode.PERSONAL_SALE_AGE_3,
    ].includes(this.appContext.purchaseMode);
  }

  @computed
  get isInSeasonalSale(): boolean {
    return [
      PurchaseMode.SALE,
      PurchaseMode.SEASONAL_EXTENDED,
      PurchaseMode.LAST_DAY_SALE,
    ].includes(this.appContext.purchaseMode);
  }

  @computed
  get isSecondUpgrade() {
    return this.appContext.isUpgrade && !this.isChangePlan;
  }

  @computed
  get isDowngradePlanOfDeclineChargeService() {
    return (
      this.isChangePlan &&
      this.experiments[
        Experiments.AddCheckoutForDowngradeDeclinedChangePlan
      ] === 'true' &&
      this.appContext.paymentStatus === PaymentStatus.CHARGE_FAILURE &&
      this.currentProduct.productChangeType === ProductChangeType.Downgrade
    );
  }

  @computed
  get isInMobileNewExperience() {
    return (
      !this.isSecondUpgrade &&
      this.experiments[Experiments.MobileNewExperience] === 'true'
    );
  }

  @computed
  get shouldShowPaymentBanner() {
    return (
      this.experiments[Experiments.OptimizedPaymentPimple] === 'true' &&
      this.getLandingTab() === PlanType.Business &&
      this.planType === PlanType.Website
    );
  }

  @computed
  get shouldStoreIntentNoTabs() {
    return (
      this.experiments[Experiments.StoreIntentNoTabs] === 'true' &&
      this.getLandingTab() === PlanType.Business &&
      this.intent === Vertical.Stores
    );
  }

  @computed
  get isAnonymous() {
    return this.appContext.isAnonymous;
  }

  @computed
  get isSaleProduct() {
    const product = new AdvancedPlansProductModel(
      this.currentProduct as AdvancedPlansProductData,
      this,
    );
    return this.isInSale && product.isProductInSale;
  }

  @computed
  get discountPercent() {
    return this.isSaleProduct ? this.appContext.saleParams!.discountPercent : 0;
  }

  @computed
  get isEnterprise(): boolean {
    return this.currentProduct.productFamily === ProductFamily.Enterprise;
  }

  @computed
  get isChangePlan(): boolean {
    const { purchaseMode, currentPlanDetails } = this.appContext;

    return (
      purchaseMode === PurchaseMode.CHANGE_PLAN &&
      !!currentPlanDetails &&
      currentPlanDetails.productFamily !== ProductFamily.Enterprise
    );
  }

  @computed
  get isSwitchPlan(): boolean {
    return this.isChangePlan || this.isSecondUpgrade;
  }
  @computed
  get isFranchPackagePicker(): boolean {
    const { country } = this.appContext;
    return (
      this.experiments[Experiments.FranchPackagePicker] === 'true' &&
      country === Country.FR
    );
  }

  @computed
  get isJapanesePackagePicker(): boolean {
    const { country, locale } = this.appContext;
    return (
      this.experiments[Experiments.JapanesePackagePicker] === 'true' &&
      country === Country.JA &&
      locale === Language.JA
    );
  }

  getChangePlanProperties = (_products: BaseProduct[]) =>
    _products.map(
      ({ id, cycles, productCode, guid }): ChangePlanProductProperties => ({
        id,
        cycles,
        productCode,
        guid,
      }),
    );

  getSignInRedirectionLink = () =>
    `//users.${
      this.isEditorX ? 'editorx' : 'wix'
    }.com/signin?postLogin=https://${
      this.isEditorX ? 'manage.editorx' : 'www.wix'
    }.com/store/plans`;

  @computed
  get currentPlanInOfferingMode(): CurrentPlanInOfferingMode {
    const { advancedPlansProducts, isDynamicOffering } = this.appContext;
    const productToCheck = this.getChangePlanProperties(advancedPlansProducts);

    const { currentPlanDetails } = this.appContext;
    if ((!this.isChangePlan && !this.isSecondUpgrade) || !currentPlanDetails) {
      return CurrentPlanInOfferingMode.None;
    }
    const matchProduct = (product) => {
      const { guid, planId } = currentPlanDetails;
      if (isDynamicOffering) {
        return product.guid === guid;
      }
      return product.id === planId;
    };
    if (productToCheck.find(matchProduct)) {
      return CurrentPlanInOfferingMode.ProductFullMatch;
    }
    if (
      productToCheck.find(
        (p) => p.productCode === currentPlanDetails.productCode,
      )
    ) {
      return CurrentPlanInOfferingMode.ProductPartialMatch;
    }
    return CurrentPlanInOfferingMode.ProductNotMatch;
  }

  @computed
  get isChangePlanToastMode(): boolean {
    return (
      this.currentPlanInOfferingMode ===
        CurrentPlanInOfferingMode.ProductNotMatch ||
      this.currentPlanInOfferingMode ===
        CurrentPlanInOfferingMode.ProductPartialMatch
    );
  }

  // this function is throttled because when changing steps before exit animation is done
  // TransitionGroup will not remove exit animation class. there is an issue in github
  // about it: https://github.com/reactjs/react-transition-group/issues/337
  changeStep: (
    change: STEP_ACTIONS,
    actionReporter: ActionTypeReporter,
    specificStep?: number,
  ) => void = throttle(
    (
      change: STEP_ACTIONS,
      actionReporter: ActionTypeReporter,
      specificStep?: number,
    ) => {
      this.previousStep = this.currentStep;
      switch (change) {
        case STEP_ACTIONS.DECREMENT:
          {
            this.decrementStep();
          }
          break;
        case STEP_ACTIONS.INCREMENT:
          {
            this.incrementStep();
          }
          break;
        case STEP_ACTIONS.SPECIFIC:
          {
            if (specificStep === undefined) {
              SentryLogger.logError(
                new Error('Tried to set specific step without providing value'),
              );
              return;
            }
            this.setStep(specificStep);
          }
          break;
        default:
          return;
      }
      actionReporter(
        BiStepNames[this.previousStep],
        BiStepNames[this.currentStep],
      );
      this.scrollToTop();
    },
    this.changeStepThrottle,
  );

  @computed
  get getDefaultCurrentProductForPlanType() {
    // TODO: using the old appContext.products, remove this.
    const { advancedPlansProducts } = this.appContext;
    const _products: BaseProduct[] = advancedPlansProducts;
    const productsPerPlanType = _products.filter(
      (product) => product.type === this.planType,
    );
    return (
      productsPerPlanType.find((prod) => !!prod.hatKey) ||
      Utils.findProductByPartialName(productsPerPlanType, 'unlimited') ||
      Utils.findProductByPartialName(productsPerPlanType, 'grow') ||
      productsPerPlanType[0] ||
      this.appContext.products[0]
    );
  }

  @computed
  get pricesIncludeTax() {
    const { country } = this.appContext;
    return taxIncludedCountries.includes(country);
  }

  get isUseDealerInegrationIntentElementsEnabled(): boolean {
    return (
      this.experiments[Experiments.useDealerIntegrationIntentBasedElements] ===
      'true'
    );
  }

  get isEditorXSpecialDiscountEnabled(): boolean {
    return (
      this.appContext.purchaseMode === PurchaseMode.SPECIAL_DISCOUNT &&
      this.isEditorX
    );
  }

  get isVoucherDisclaimerHidden(): boolean {
    const { hasGoogleVoucher } = this.appContext;
    return !hasGoogleVoucher;
  }

  get googleVoucherAmounts(): VoucherSettings {
    const voucherDefualts: VoucherSettings = {
      amountToSpend: 25,
      amount: 75,
      availableInUserCountry: true,
      currencyCode: 'USD',
      voucherType: VouchersType.GOOGLE_ADS,
    };
    const googleVoucher =
      this.appContext.voucherSettings?.find(
        (voucherSetting) => voucherSetting.voucherType === 'GOOGLE_ADS',
      ) || voucherDefualts;
    const { amountToSpend, amount, currencyCode } = googleVoucher;
    return {
      ...voucherDefualts,
      amount,
      amountToSpend,
      currencyCode,
    };
  }

  getLandingTab = () => {
    const parsedDealerOffers = this.appContext.parsedDealerOffers;
    const landingTabOffer = (this.isSecondUpgrade
      ? parsedDealerOffers.get(RealEstateId.AppIntentElementsSecondUpgrade)
      : parsedDealerOffers.get(
          RealEstateId.AppIntentElementsInitialPurchase,
        )) as ParsedAppIntentElements;

    if (this.isBusinessTab) {
      return PlanType.Business;
    }

    return this.isUseDealerInegrationIntentElementsEnabled
      ? landingTabOffer.landingTab
      : PlanType.Website;
  };

  isSelecetedPlan = (product: AdvancedPlansProductData) =>
    this.currentProduct.id === product.id;

  @computed
  get isSepaCountry() {
    return (
      this.experiments[Experiments.EnableSepaNoRefundDisclaimers] === 'true' &&
      SEPA_ENABLED_COUNTRIES.includes(this.appContext.country)
    );
  }

  get intent() {
    const { parsedDealerOffers, verticalName } = this.appContext;
    const intentOffer = (this.isSecondUpgrade
      ? parsedDealerOffers.get(RealEstateId.AppIntentElementsSecondUpgrade)
      : parsedDealerOffers.get(
          RealEstateId.AppIntentElementsInitialPurchase,
        )) as ParsedAppIntentElements;

    return this.isUseDealerInegrationIntentElementsEnabled
      ? intentOffer.intent
      : verticalName;
  }

  get advancedPlansBusinessProducts(): AdvancedPlansProductData[] {
    const { advancedPlansProducts } = this.appContext;
    return advancedPlansProducts.filter(
      ({ type }) => type === PlanType.Business,
    );
  }

  get advancedPlansWebsiteProducts(): AdvancedPlansProductData[] {
    const { advancedPlansProducts } = this.appContext;
    return advancedPlansProducts.filter(
      ({ type }) => type === PlanType.Website,
    );
  }

  get defaultProductSelection(): AdvancedPlansProductData {
    const products =
      this.planType === PlanType.Website
        ? this.advancedPlansWebsiteProducts
        : this.advancedPlansBusinessProducts;
    let defualtProduct = products[0];
    products.forEach((product) => {
      const _product = new AdvancedPlansProductModel(product, this);
      defualtProduct = _product.isSelected() ? product : defualtProduct;
    });
    return defualtProduct;
  }
}
